import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowRight, Clock } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { COMPANY } from "@/data/site";

export default function Contact() {
  useSEO("Contact Us", "Get in touch with Cargowyn Logistics Group. Call, email, or send us a message about your cross-border freight needs.");
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in name, email and message."); return; }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll reply shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <section className="bg-[#0B192C] text-white pt-24 pb-20 relative overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 noise opacity-[0.05]" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
          <div className="flex items-center gap-3 mb-5"><span className="h-[2px] w-10 bg-[#FF5A00]" /><span className="font-head uppercase tracking-[0.3em] text-[#FF5A00] text-sm font-700">Get In Touch</span></div>
          <h1 className="font-head font-black uppercase text-6xl md:text-8xl tracking-tighter leading-[0.85]">Let's talk freight</h1>
        </div>
      </section>

      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 space-y-6">
            {[{ icon: Phone, t: "Call Us", v: COMPANY.phone, href: COMPANY.phoneHref },
              { icon: Mail, t: "Email Us", v: COMPANY.email, href: `mailto:${COMPANY.email}` },
              { icon: MapPin, t: "Based In", v: COMPANY.region },
              { icon: Clock, t: "Hours", v: "Mon–Fri · 24/7 for active loads" }].map((c, i) => (
              <div key={c.t} className="flex items-start gap-4 bg-white border-2 border-[#0B192C]/10 p-6" data-testid={`contact-info-${i}`}>
                <div className="w-12 h-12 bg-[#FF5A00] flex items-center justify-center flex-shrink-0"><c.icon className="text-white" size={22} /></div>
                <div>
                  <div className="font-head uppercase tracking-wide text-xs font-700 text-[#94A3B8]">{c.t}</div>
                  {c.href ? <a href={c.href} className="font-head font-700 text-lg text-[#0B192C] hover:text-[#FF5A00]">{c.v}</a> : <div className="font-head font-700 text-lg text-[#0B192C]">{c.v}</div>}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={submit} className="lg:col-span-3 bg-white border-2 border-[#0B192C] p-8 lg:p-10 space-y-6" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-6">
              <div><Label className="font-head uppercase tracking-wide text-xs font-700">Name *</Label><Input value={form.name} onChange={set("name")} data-testid="contact-name" className="mt-2 rounded-none border-2 border-[#0B192C]/15 h-12 focus-visible:ring-0 focus-visible:border-[#FF5A00]" /></div>
              <div><Label className="font-head uppercase tracking-wide text-xs font-700">Email *</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="contact-email" className="mt-2 rounded-none border-2 border-[#0B192C]/15 h-12 focus-visible:ring-0 focus-visible:border-[#FF5A00]" /></div>
              <div><Label className="font-head uppercase tracking-wide text-xs font-700">Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="contact-phone" className="mt-2 rounded-none border-2 border-[#0B192C]/15 h-12 focus-visible:ring-0 focus-visible:border-[#FF5A00]" /></div>
              <div><Label className="font-head uppercase tracking-wide text-xs font-700">Subject</Label><Input value={form.subject} onChange={set("subject")} data-testid="contact-subject" className="mt-2 rounded-none border-2 border-[#0B192C]/15 h-12 focus-visible:ring-0 focus-visible:border-[#FF5A00]" /></div>
            </div>
            <div><Label className="font-head uppercase tracking-wide text-xs font-700">Message *</Label><Textarea rows={5} value={form.message} onChange={set("message")} data-testid="contact-message" className="mt-2 rounded-none border-2 border-[#0B192C]/15 focus-visible:ring-0 focus-visible:border-[#FF5A00]" /></div>
            <button type="submit" disabled={loading} data-testid="contact-submit-button" className="w-full bg-[#FF5A00] text-white font-head font-700 uppercase tracking-[0.2em] py-4 flex items-center justify-center gap-3 hover:bg-[#E65100] transition-colors disabled:opacity-60">{loading ? "Sending..." : <>Send Message <ArrowRight size={18} /></>}</button>
          </form>
        </div>
      </section>
    </div>
  );
}
